/**
 * Runtime environment of the three SPAs, derived from the page's own hostname
 * (ADR-0084).
 *
 * Production never builds images: it deploys the exact web bundle that
 * staging built and tested for the same release. So nothing environment-
 * specific may be compiled into that bundle. Every deployment uses one host
 * shape: the student app at `<base>`, the others at `admin.<base>`,
 * `manager.<base>` and `api.<base>`. The page therefore knows its API and its
 * sibling apps from where it was loaded.
 *
 * The deployment bases are an explicit allow-list. Any other host (Vite dev
 * servers, Storybook, unit tests, an unlisted subdomain) is `development`,
 * never production, and uses the `VITE_*` overrides the app passes in. On a
 * deployment host the overrides are ignored, so a bundle built with stray
 * values still talks to its own environment.
 *
 * Keep absolute deployment URLs out of this file and its comments:
 * infra/prod/deploy.sh refuses a bundle that contains an absolute staging URL.
 */
export const KNOWN_DEPLOYMENT_BASES = Object.freeze({
    'coach.cooori.com': 'production',
    'staging.coach.cooori.com': 'staging',
    // The local production-like stack behind Caddy (make staging-local-up).
    'coach.localhost': 'local',
});
/** The API port of a development server: make dev-api, scripts/dev.sh. */
export const DEVELOPMENT_API_PORT = 8000;
const SPA_SURFACE_PREFIX = /^(?:admin|manager)\./;
function normaliseHostname(hostname) {
    return hostname.trim().toLowerCase().replace(/\.$/, '');
}
function override(value) {
    const trimmed = value?.trim() ?? '';
    return trimmed === '' ? null : trimmed.replace(/\/+$/, '');
}
export function resolveRuntimeEnvironment(location, overrides = {}) {
    const hostname = normaliseHostname(location.hostname);
    const base = hostname.replace(SPA_SURFACE_PREFIX, '');
    const name = KNOWN_DEPLOYMENT_BASES[base];
    if (name !== undefined) {
        // Deployment hosts are always served over TLS by Caddy.
        return {
            name,
            apiBaseUrl: `https://api.${base}`,
            webBaseUrl: `https://${base}`,
            adminBaseUrl: `https://admin.${base}`,
            managerBaseUrl: `https://manager.${base}`,
        };
    }
    // Same hostname as the page (localhost stays localhost, 127.0.0.1 stays
    // 127.0.0.1): the session cookie is SameSite=strict, so mixing the two
    // would make every authenticated call cross-site.
    return {
        name: 'development',
        apiBaseUrl: override(overrides.apiBaseUrl) ?? `${location.protocol}//${hostname}:${DEVELOPMENT_API_PORT}`,
        webBaseUrl: override(overrides.webBaseUrl),
        adminBaseUrl: override(overrides.adminBaseUrl),
        managerBaseUrl: override(overrides.managerBaseUrl),
    };
}
/**
 * The browser Sentry DSN to use. The DSN is a build-time value: the release
 * build supplies the coach-web project's DSN, and local builds leave it
 * empty. One bundle serves every environment (ADR-0084), so staging and
 * production both report into coach-web, told apart by the hostname-derived
 * `environment` each SPA sets (ADR-0089).
 */
export function browserSentryDsn(buildDsn) {
    return buildDsn?.trim() ?? '';
}
/** The unreleased landing page is previewed everywhere except production. */
export function isLandingPagePreviewAllowed(name) {
    return name !== 'production';
}
//# sourceMappingURL=runtime-environment.js.map